/**
 * Completion for CHECK constraint enum values.
 * Suggests allowed values when typing after `column = '` or `column IN ('`.
 *
 * checkConstraints format: { tableName: { columnName: ["VAL1", "VAL2", ...] } }
 */

import type {Suggestion} from '@/components/ui/completion-editor'

interface CheckCompletionIndex {
    columnValues: Record<string, Set<string>>
    qualifiedValues: Record<string, string[]>
}

export function buildCheckIndex(checkConstraints: Record<string, Record<string, string[]>>): CheckCompletionIndex {
    const columnValues: Record<string, Set<string>> = {}
    for (const columns of Object.values(checkConstraints)) {
        for (const [column, values] of Object.entries(columns)) {
            if (!columnValues[column]) {
                columnValues[column] = new Set()
            }
            for (const v of values) {
                columnValues[column].add(v)
            }
        }
    }

    const qualifiedValues: Record<string, string[]> = {}
    for (const [table, columns] of Object.entries(checkConstraints)) {
        for (const [column, values] of Object.entries(columns)) {
            qualifiedValues[`${table}.${column}`] = values
        }
    }

    return {columnValues, qualifiedValues}
}

export function matchCheckConstraint(textBefore: string, index: CheckCompletionIndex): Suggestion[] {
    const match = textBefore.match(
        /(?:(\w+)\.)?(\w+)\s+(?:=|IN\s*\()\s*'(\w*)$/i
    )
    if (!match) return []

    const alias = match[1]?.toLowerCase()
    const column = match[2].toLowerCase()
    const partial = match[3].toLowerCase()

    let values: string[] | undefined
    if (alias) {
        values = index.qualifiedValues[`${alias}.${column}`]
        if (!values) {
            const colSet = index.columnValues[column]
            values = colSet ? [...colSet] : undefined
        }
    } else {
        const colSet = index.columnValues[column]
        values = colSet ? [...colSet] : undefined
    }

    if (!values || values.length === 0) return []

    return values
        .filter(v => v.toLowerCase().startsWith(partial))
        .map(v => ({value: v, description: 'enum'}))  
}
